import { useState } from 'react'
import {
  FileText,
  Image,
  FileSpreadsheet,
  File,
  Download,
  Eye,
  Pencil,
  Trash2,
  Calendar,
  AlertTriangle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { Documento, DocumentoConCliente } from '@/types/database'
import { CATEGORIAS_DOCUMENTO, formatFileSize } from '@/types/documentos'
import { cn } from '@/lib/utils'

interface DocumentoCardProps {
  documento: Documento | DocumentoConCliente
  onVer?: (documento: Documento) => void
  onDescargar?: (documento: Documento) => void
  onEditar?: (documento: Documento) => void
  onEliminar?: (documento: Documento) => void
  showCliente?: boolean
  compact?: boolean
  // Props para selección múltiple
  selectable?: boolean
  selected?: boolean
  onSelectionChange?: (selected: boolean) => void
}

function getDocumentoIcon(mimeType: string | null, className?: string) {
  const iconClass = cn('h-8 w-8', className)
  if (!mimeType) return <File className={cn(iconClass, 'text-gray-500')} />
  if (mimeType.startsWith('image/')) return <Image className={cn(iconClass, 'text-blue-500')} />
  if (mimeType === 'application/pdf') return <FileText className={cn(iconClass, 'text-red-500')} />
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel'))
    return <FileSpreadsheet className={cn(iconClass, 'text-green-500')} />
  if (mimeType.includes('word')) return <FileText className={cn(iconClass, 'text-blue-600')} />
  return <File className={cn(iconClass, 'text-gray-500')} />
}

function getDiasRestantesInfo(diasRestantes: number | null | undefined) {
  if (diasRestantes === null || diasRestantes === undefined) return null

  if (diasRestantes <= 0) {
    return { text: 'Vencido', className: 'text-red-600 bg-red-100' }
  }
  if (diasRestantes <= 7) {
    return { text: `${diasRestantes} días`, className: 'text-red-600 bg-red-100' }
  }
  if (diasRestantes <= 15) {
    return { text: `${diasRestantes} días`, className: 'text-orange-600 bg-orange-100' }
  }
  if (diasRestantes <= 30) {
    return { text: `${diasRestantes} días`, className: 'text-yellow-600 bg-yellow-100' }
  }
  return null
}

export function DocumentoCard({
  documento,
  onVer,
  onDescargar,
  onEditar,
  onEliminar,
  showCliente = false,
  compact = false,
  selectable = false,
  selected = false,
  onSelectionChange,
}: DocumentoCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const categoriaConfig = (documento.categoria && CATEGORIAS_DOCUMENTO[documento.categoria])
    || { label: documento.categoria || 'Sin categoría', color: 'bg-gray-100 text-gray-800' }

  const diasRestantesInfo =
    'dias_restantes' in documento ? getDiasRestantesInfo(documento.dias_restantes) : null

  const clienteNombre = 'cliente_nombre' in documento ? documento.cliente_nombre : null

  const handleEliminar = () => {
    setShowDeleteDialog(false)
    onEliminar?.(documento)
  }

  if (compact) {
    return (
      <>
        <div className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded-lg transition-colors group">
          {getDocumentoIcon(documento.mime_type, 'h-6 w-6')}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{documento.nombre}</p>
            <p className="text-xs text-muted-foreground">
              {formatFileSize(documento.archivo_size)} • {categoriaConfig.label}
            </p>
          </div>
          <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 transition-opacity">
            {onVer && (
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onVer(documento)}>
                <Eye className="h-3.5 w-3.5" />
              </Button>
            )}
            {onDescargar && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onDescargar(documento)}
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar documento</AlertDialogTitle>
              <AlertDialogDescription>
                ¿Estás seguro de que deseas eliminar "{documento.nombre}"? Esta acción no se puede
                deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleEliminar} className="bg-destructive text-destructive-foreground">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    )
  }

  return (
    <>
      <div
        className={cn(
          'flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/30 transition-colors group',
          selected && 'bg-accent/10 border-accent'
        )}
      >
        {/* Checkbox de selección */}
        {selectable && (
          <Checkbox
            checked={selected}
            onCheckedChange={(checked) => onSelectionChange?.(checked === true)}
            className="mt-1 shrink-0"
            aria-label={`Seleccionar ${documento.nombre}`}
          />
        )}

        {getDocumentoIcon(documento.mime_type)}

        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{documento.nombre}</p>
              {documento.nombre !== documento.nombre_original && (
                <p className="text-xs text-muted-foreground truncate">
                  Archivo: {documento.nombre_original}
                </p>
              )}
            </div>

            {/* Botones de acción directos - always visible on mobile, hover on desktop */}
            <TooltipProvider delayDuration={300}>
              <div className="flex items-center gap-1 shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 transition-opacity">
                {onVer && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onVer(documento)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Vista previa</TooltipContent>
                  </Tooltip>
                )}
                {onDescargar && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onDescargar(documento)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Descargar</TooltipContent>
                  </Tooltip>
                )}
                {onEditar && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onEditar(documento)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar</TooltipContent>
                  </Tooltip>
                )}
                {onEliminar && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setShowDeleteDialog(true)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Eliminar</TooltipContent>
                  </Tooltip>
                )}
              </div>
            </TooltipProvider>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>{formatFileSize(documento.archivo_size)}</span>
            <span>•</span>
            <Badge variant="secondary" className={cn('text-xs', categoriaConfig.color)}>
              {categoriaConfig.label}
            </Badge>

            {showCliente && clienteNombre && (
              <>
                <span>•</span>
                <span className="truncate">{clienteNombre}</span>
              </>
            )}
          </div>

          {documento.fecha_vencimiento && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">
                Vence: {new Date(documento.fecha_vencimiento).toLocaleDateString('es-AR')}
              </span>
              {diasRestantesInfo && (
                <Badge variant="secondary" className={cn('text-xs', diasRestantesInfo.className)}>
                  {diasRestantesInfo.text === 'Vencido' && (
                    <AlertTriangle className="h-3 w-3 mr-1" />
                  )}
                  {diasRestantesInfo.text}
                </Badge>
              )}
            </div>
          )}

          {documento.created_at && (
            <p className="text-xs text-muted-foreground">
              Subido el {new Date(documento.created_at).toLocaleDateString('es-AR')}
            </p>
          )}
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar documento</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar "{documento.nombre}"? Esta acción no se puede
              deshacer y el archivo será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleEliminar} className="bg-destructive text-destructive-foreground">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
